import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import { hasRole } from './constants/roles';
export const STORE_KEYS = ['soccerpost', 'surf', 'rush', 'citysports', 'u90', 'b2b', 'stefans'];
export const STORE_DISPLAY_NAMES = {
  soccerpost: 'Soccer Post',
  surf:       'Surf',
  rush:       'Rush',
  citysports: 'City Sports',
  u90:        'Upper 90',
  b2b:        'B2B',
  stefans:    'Stefans',
  all:        'All Stores',
};

export const isValidStoreSlug = (slug) =>
  Boolean(slug) && (STORE_KEYS.includes(slug) || slug === 'all');

// Extract the store slug from a pathname of the form /store/:slug/...
export const getStoreSlugFromPath = (pathname) => {
  if (!pathname) return null;
  const m = pathname.match(/^\/store\/([^/]+)(?:\/|$)/);
  return m ? m[1] : null;
};

// Build a path that swaps (or inserts) the store slug. Preserves the
// sub-path after /store/:slug/ when present; otherwise falls back to `orders`.
export const buildStorePath = (slug, currentPathname = '/') => {
  const m = currentPathname.match(/^\/store\/[^/]+(\/.*)?$/);
  const suffix = m ? (m[1] || '/orders') : '/orders';
  return `/store/${slug}${suffix === '/' ? '/orders' : suffix}`;
};

const StoreContext = createContext(null);
export { StoreContext };

export function StoreProvider({ user, children }) {
  const userStores = [...new Set((user?.locations || []).map(l => l.store_key).filter(Boolean))];
  // customer_service / finance have all-stores access just like admin (no per-store assignments).
  const ownStores = (hasRole(user, ['admin']) || hasRole(user, ['customer_service']) || hasRole(user, ['finance']))
    ? STORE_KEYS
    : userStores.length > 0 ? userStores : ['soccerpost'];
  const accessibleStores = ownStores.length > 1 ? [...ownStores, 'all'] : ownStores;

  const [activeStore, setActiveStoreRaw] = useState(() => {
    // Seed from URL path first (new), then query param (legacy), then localStorage.
    const urlPathStore = getStoreSlugFromPath(window.location.pathname);
    if (urlPathStore && accessibleStores.includes(urlPathStore)) return urlPathStore;
    const urlQueryStore = new URLSearchParams(window.location.search).get('store');
    if (urlQueryStore && accessibleStores.includes(urlQueryStore)) return urlQueryStore;
    const saved = localStorage.getItem('ops_active_store');
    return saved && accessibleStores.includes(saved) ? saved : accessibleStores[0];
  });

  // When user's accessible stores change (e.g. after login verify populates locations),
  // reset activeStore if current selection is no longer valid.
  useEffect(() => {
    if (!accessibleStores.includes(activeStore)) {
      const saved = localStorage.getItem('ops_active_store');
      const next = saved && accessibleStores.includes(saved) ? saved : accessibleStores[0];
      setActiveStoreRaw(next);
    }
  }, [accessibleStores.join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  const setActiveStore = (key) => {
    if (!accessibleStores.includes(key)) return;
    localStorage.setItem('ops_active_store', key);
    setActiveStoreRaw(key);
  };

  return (
    <StoreContext.Provider value={{ activeStore, setActiveStore, accessibleStores, STORE_DISPLAY_NAMES }}>
      {children}
    </StoreContext.Provider>
  );
}

// useStore returns the effective active store for the current route.
// If we're on a /store/:slug/... path AND that slug is accessible, the URL wins;
// otherwise we fall back to the state-based value (legacy routes).
export function useStore() {
  const ctx = useContext(StoreContext);
  if (!ctx) throw new Error('useStore must be used inside <StoreProvider>');
  const location = useLocation();
  const urlSlug = getStoreSlugFromPath(location.pathname);
  const effective = (urlSlug && ctx.accessibleStores.includes(urlSlug))
    ? urlSlug
    : ctx.activeStore;
  return { ...ctx, activeStore: effective };
}
